import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Lightbulb, Loader2, Send, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERKS = [
  "We market world-famous 'As Seen on TV' brands",
  "End-to-end: development, branding & distribution",
  "Your idea reaches retail shelves worldwide",
];

export const InventorSection = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", invention: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setMessage("");
    try {
      const { data } = await axios.post(`${API}/inquiries`, form);
      setStatus("success");
      setMessage(data.message || "Your invention has been received!");
      setForm({ name: "", email: "", invention: "" });
    } catch (err) {
      setStatus("error");
      setMessage(
        err.response?.data?.detail?.[0]?.msg ||
          err.response?.data?.detail ||
          "Something went wrong. Please try again."
      );
    }
  };

  const closeAndReset = (next) => {
    setOpen(next);
    if (!next) {
      setStatus("idle");
      setMessage("");
    }
  };

  return (
    <section
      id="inventor"
      data-testid="inventor-section"
      className="relative bg-brand-bg py-24 border-t border-brand-border overflow-hidden"
    >
      <div className="pointer-events-none absolute -right-32 top-10 h-[420px] w-[420px] rounded-full bg-[#8DC63F]/10 blur-[130px]" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#13355E]/50 to-[#050A15] p-10 lg:p-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-[#8DC63F]/30 bg-[#8DC63F]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.25em] text-[#8DC63F]">
                <Lightbulb size={14} /> Inventor Connection
              </span>
              <h2 className="mt-6 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
                Got an invention?
              </h2>
              <p className="mt-4 max-w-md text-slate-400">
                We're always looking for the next breakthrough product. If you have
                a unique idea that solves an everyday problem, we want to hear it —
                and help bring it to millions of homes.
              </p>
              <ul className="mt-8 space-y-3">
                {PERKS.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-slate-300">
                    <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-[#8DC63F]" />
                    {p}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:justify-self-end"
            >
              <div className="rounded-2xl border border-white/10 bg-[#050A15]/60 p-8 text-center">
                <h3 className="font-heading text-2xl font-semibold text-white">
                  Pitch your idea
                </h3>
                <p className="mt-2 text-sm text-slate-400">
                  It takes two minutes. No idea is too small.
                </p>
                <button
                  data-testid="open-inventor-form"
                  onClick={() => setOpen(true)}
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#8DC63F] px-7 py-3.5 text-sm font-semibold text-[#050A15] hover:bg-[#7AB034] transition-colors"
                >
                  Submit Your Invention <Send size={16} />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={closeAndReset}>
        <DialogContent
          data-testid="inventor-dialog"
          className="bg-[#0A1424] border-white/10 text-white sm:max-w-lg"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              Got an Invention?
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Tell us about your idea and our product team will reach out.
            </DialogDescription>
          </DialogHeader>

          {status === "success" ? (
            <div data-testid="inventor-success" className="py-8 text-center">
              <CheckCircle2 size={48} className="mx-auto text-[#8DC63F]" />
              <p className="mt-4 text-lg font-medium text-white">Thank you!</p>
              <p className="mt-2 text-sm text-slate-400">{message}</p>
              <button
                onClick={() => closeAndReset(false)}
                className="mt-6 inline-flex items-center rounded-full bg-[#8DC63F] px-6 py-2.5 text-sm font-semibold text-[#050A15] hover:bg-[#7AB034] transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-300" htmlFor="inv-name">
                  Full name
                </label>
                <input
                  id="inv-name"
                  data-testid="inventor-name-input"
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Jane Inventor"
                  className="mt-1.5 w-full rounded-lg border border-white/10 bg-[#050A15] px-4 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#8DC63F]/60"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-300" htmlFor="inv-email">
                  Email
                </label>
                <input
                  id="inv-email"
                  data-testid="inventor-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  placeholder="jane@example.com"
                  className="mt-1.5 w-full rounded-lg border border-white/10 bg-[#050A15] px-4 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#8DC63F]/60"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-300" htmlFor="inv-idea">
                  Describe your invention
                </label>
                <textarea
                  id="inv-idea"
                  data-testid="inventor-idea-input"
                  required
                  minLength={10}
                  rows={4}
                  value={form.invention}
                  onChange={update("invention")}
                  placeholder="What problem does it solve? How does it work?"
                  className="mt-1.5 w-full resize-none rounded-lg border border-white/10 bg-[#050A15] px-4 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#8DC63F]/60"
                />
              </div>

              {status === "error" && (
                <p data-testid="inventor-error" className="text-sm text-red-400">
                  {message}
                </p>
              )}

              <DialogFooter>
                <button
                  type="submit"
                  data-testid="inventor-submit-button"
                  disabled={status === "loading"}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#8DC63F] px-7 py-3 text-sm font-semibold text-[#050A15] hover:bg-[#7AB034] transition-colors disabled:opacity-60"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      Submit Your Invention <Send size={16} />
                    </>
                  )}
                </button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
